<template>

  <div class="invoice-dense-tab invoice-overview-tab">

    <div class="invoice-dense-toolbar">

      <div class="invoice-dense-toolbar__search">

        <b-input-group size="sm">

          <template #prepend>

            <b-input-group-text class="invoice-dense-search-icon">

              <feather-icon icon="SearchIcon" size="14" />

            </b-input-group-text>

          </template>

          <b-form-input

            v-model="search"

            placeholder="Search invoice #, client, status…"

            :debounce="200"

          />

          <template v-if="search" #append>

            <b-button size="sm" variant="outline-secondary" @click="search = ''">

              <feather-icon icon="XIcon" size="14" />

            </b-button>

          </template>

        </b-input-group>

      </div>

      <div class="invoice-dense-toolbar__meta">

        <span v-if="!loading">{{ filteredItems.length }} invoice{{ filteredItems.length === 1 ? '' : 's' }}</span>

        <span v-else>Loading…</span>

      </div>

    </div>



    <div class="invoice-dense-table-wrap">

      <b-table

        :items="filteredItems"

        :fields="fields"

        :per-page="perPage"

        :current-page="currentPage"

        hover

        responsive

        :busy="loading"

        show-empty

        class="invoice-dense-table mb-0"

      >

        <template #table-busy>

          <div class="invoice-dense-state invoice-dense-state--loading">

            <b-spinner small class="mr-50" />

            <span>Loading invoices…</span>

          </div>

        </template>



        <template #empty>

          <div class="invoice-dense-state">

            <div class="invoice-dense-empty-icon">

              <feather-icon icon="FileTextIcon" size="28" />

            </div>

            <h6 class="mb-25">No invoices found</h6>

            <p class="text-muted mb-1">

              {{ search ? 'Try a different search or clear filters.' : 'Adjust filters or create a new invoice to get started.' }}

            </p>

            <b-button

              v-if="canCreate && !search"

              size="sm"

              variant="primary"

              @click="$emit('create-invoice')"

            >

              <feather-icon icon="PlusIcon" size="14" class="mr-25" />

              Create Invoice

            </b-button>

            <b-button

              v-else-if="search"

              size="sm"

              variant="outline-secondary"

              @click="search = ''"

            >

              Clear search

            </b-button>

          </div>

        </template>



        <template #cell(invoice_info)="{ item }">

          <div class="inv-cell-stack">

            <b-link class="inv-invoice-link" @click="$emit('view-invoice', item)">

              {{ item.invoice_id || item.invoice_number || 'N/A' }}

            </b-link>

            <small class="text-muted">{{ formatDate(item.invoice_date) }}</small>

            <small class="text-muted">{{ item.category || '—' }}</small>

          </div>

        </template>



        <template #cell(client_candidate)="{ item }">

          <div class="inv-cell-stack text-wrap text-break">

            <span class="inv-primary-text text-break">{{ item.client_name || 'N/A' }}</span>

            <small v-if="item.client_gst" class="inv-secondary-text text-break">{{ item.client_gst }}</small>

            <small
              v-if="item.candidate_display && item.candidate_display !== '—'"
              class="inv-candidate-line text-break"
            >

              <template v-if="item.candidate_linkable">

                <b-link class="text-primary text-break" @click="$emit('view-candidate', item.candidate_id)">

                  {{ item.candidate_display }}

                </b-link>

              </template>

              <template v-else>

                <span
                  class="text-break"
                  :class="item.candidate_id && !item.candidate_name ? 'text-warning' : 'inv-secondary-text'"
                >

                  {{ item.candidate_display }}

                </span>

              </template>

            </small>

          </div>

        </template>



        <template #cell(status)="{ item }">

          <div class="inv-cell-stack align-items-start">

            <b-badge :variant="getStatusVariant(item.status)" class="inv-status-badge">

              {{ statusWithRevision(item) }}

            </b-badge>

            <span

              v-if="revisionCountOf(item) > 0 && !isRevisedRow(item)"

              class="inv-rev-pill"

            >

              Rev {{ revisionCountOf(item) }}

            </span>

            <b-button

              v-if="showHistoryAction(item)"

              size="sm"

              variant="link"

              class="inv-inline-action p-0"

              @click="$emit('view-history', item)"

            >

              View history

            </b-button>

            <span

              v-if="item.has_signed_invoice || item.signed_invoice || item.signed_invoice_path"

              class="inv-signed-pill"

            >

              <feather-icon icon="CheckCircleIcon" size="11" class="mr-25" />

              Signed

            </span>

          </div>

        </template>



        <template #cell(base_amount)="{ item }">

          <span class="inv-amount">₹{{ formatIndianNumber(item.base_amount || 0) }}</span>

        </template>

        <template #cell(amount_receivable)="{ item }">

          <span class="inv-amount">₹{{ formatIndianNumber(item.total_amount || 0) }}</span>

        </template>

        <template #cell(outstanding_amount)="{ item }">

          <span class="inv-amount" :class="item.outstanding_amount > 0 ? 'text-danger' : 'text-success'">

            ₹{{ formatIndianNumber(item.outstanding_amount || 0) }}

          </span>

        </template>

        <template #cell(due_date)="{ item }">

          <span :class="isOverdueRow(item) ? 'text-danger font-weight-bold' : ''">

            {{ formatDate(item.due_date) }}

          </span>

        </template>

        <template #cell(gstr1_status)="{ item }">

          <div class="inv-cell-stack align-items-start">

            <b-badge :variant="getGstr1StatusVariant(item.gstr1_status)" class="inv-status-badge">

              {{ item.gstr1_status || 'Not Ready' }}

            </b-badge>

            <b-button

              v-if="$can('update','invoice') && item.einvoice_filed === true"

              size="sm"

              variant="link"

              class="inv-inline-action p-0"

              @click="$emit('update-gstr', item)"

            >

              Update GSTR

            </b-button>

          </div>

        </template>

        <template #cell(einvoice_status)="{ item }">

          <div class="inv-cell-stack align-items-start">

            <b-badge

              :variant="item.einvoice_filed === true ? 'success' : item.einvoice_filed === false ? 'danger' : 'warning'"

              class="inv-status-badge"

            >

              {{ item.einvoice_filed === true ? 'Filed' : item.einvoice_filed === false ? 'Not Filed' : 'Pending' }}

            </b-badge>

            <b-button

              size="sm"

              variant="link"

              class="inv-inline-action p-0"

              @click="$emit('update-einvoice', item)"

            >

              Update E-Inv

            </b-button>

          </div>

        </template>



        <template #cell(actions)="{ item }">

          <div class="inv-row-actions">

            <b-button

              size="sm"

              variant="flat-primary"

              class="btn-icon"
              aria-label="View"

              @click="$emit('view-invoice', item)"

            >

              <feather-icon icon="EyeIcon" size="15" />

            </b-button>

            <span
              v-b-tooltip.hover
              :title="canDownload ? 'Download PDF' : downloadDeniedTooltip"
            >
              <b-button

                size="sm"

                variant="flat-secondary"

                class="btn-icon"
                aria-label="Download PDF"
                :disabled="!canDownload"

                @click="$emit('download-pdf', item)"

              >

                <feather-icon icon="FileTextIcon" size="15" />

              </b-button>
            </span>

            <b-dropdown

              variant="link"

              toggle-class="text-decoration-none p-0 inv-more-toggle"

              no-caret

              right

              boundary="window"

            >

              <template #button-content>

                <feather-icon icon="MoreVerticalIcon" size="16" class="text-body" />

              </template>

              <!-- View + Download PDF stay as icon buttons above; omit from overflow menu -->

              <b-dropdown-item
                v-if="canCreate"
                @click="$emit('duplicate-invoice', item)"
              >
                <feather-icon icon="CopyIcon" size="14" class="mr-1" />

                Duplicate for next month

              </b-dropdown-item>

              <b-dropdown-item @click="$emit('upload-signed', item)">
                <feather-icon icon="UploadIcon" size="14" class="mr-1" />

                Upload Signed Invoice

              </b-dropdown-item>

              <b-dropdown-item v-if="canDownload" @click="$emit('download-json', item)">

                <feather-icon icon="DownloadIcon" size="14" class="mr-1" />

                Download JSON

              </b-dropdown-item>

              <b-dropdown-item v-if="canDownload" @click="$emit('download-excel', item)">

                <feather-icon icon="FileIcon" size="14" class="mr-1" />

                Download Excel

              </b-dropdown-item>

              <b-dropdown-item v-if="!['Paid'].includes(item.status)" @click="$emit('record-payment', item)">

                <feather-icon icon="DollarSignIcon" size="14" class="mr-1" />

                Record Payment

              </b-dropdown-item>

              <b-dropdown-item @click="$emit('update-status', item)">

                <feather-icon icon="EditIcon" size="14" class="mr-1" />

                Update Status

              </b-dropdown-item>

              <b-dropdown-item v-if="!['Disputed','Cancelled'].includes(item.status)" @click="$emit('dispute', item)">

                <feather-icon icon="AlertCircleIcon" size="14" class="mr-1" />

                Mark as Disputed

              </b-dropdown-item>

            </b-dropdown>

          </div>

        </template>

      </b-table>

    </div>



    <div v-if="filteredItems.length > perPage" class="invoice-dense-pagination">

      <b-pagination

        v-model="currentPage"

        :total-rows="filteredItems.length"

        :per-page="perPage"

        align="right"

        size="sm"

        class="mb-0"

      />

    </div>

  </div>

</template>



<script>

import {

  BFormInput, BInputGroup, BInputGroupText, BTable, BBadge, BButton,

  BDropdown, BDropdownItem, BLink, BSpinner, BPagination, VBTooltip

} from 'bootstrap-vue'

import invoiceFormatMixin from '@/mixins/invoiceFormatMixin'
import { INVOICE_DOWNLOAD_DENIED_TOOLTIP } from '@/utils/invoice-permissions'
import {
  formatStatusWithRevisionCount,
  getInvoiceRevisionCount,
  isRevisedStatus,
} from '@/utils/invoice-revision-count'



export default {

  name: 'InvoiceOverviewTab',

  components: {

    BFormInput, BInputGroup, BInputGroupText, BTable, BBadge, BButton,

    BDropdown, BDropdownItem, BLink, BSpinner, BPagination

  },

  directives: { 'b-tooltip': VBTooltip },

  mixins: [invoiceFormatMixin],

  props: {

    items: { type: Array, default: () => [] },

    fields: { type: Array, default: () => [] },

    loading: Boolean,

    perPage: { type: Number, default: 20 },

    canCreate: { type: Boolean, default: false },

    canDownload: { type: Boolean, default: false }

  },

  data() {

    return { search: '', currentPage: 1, downloadDeniedTooltip: INVOICE_DOWNLOAD_DENIED_TOOLTIP }

  },

  computed: {

    filteredItems() {

      if (!this.search) return this.items

      const q = this.search.toLowerCase()

      return this.items.filter(i =>

        (i.invoice_id || '').toLowerCase().includes(q) ||

        (i.invoice_number || '').toLowerCase().includes(q) ||

        (i.client_name || '').toLowerCase().includes(q) ||

        (i.status || '').toLowerCase().includes(q) ||

        (i.candidate_display || '').toLowerCase().includes(q)

      )

    }

  },

  watch: {

    search() {

      this.currentPage = 1

    },

    items() {

      this.currentPage = 1

    }

  },

  methods: {

    isOverdueRow(item) {

      if (!this.isOverdue(item && (item.due_date || item.dueDate))) return false

      const outstanding = item && item.outstanding_amount

      if (outstanding == null || outstanding === '') return true

      const n = parseFloat(outstanding)

      return Number.isNaN(n) ? true : n > 0

    },

    revisionCountOf(item) {

      return getInvoiceRevisionCount(item)

    },

    isRevisedRow(item) {

      return isRevisedStatus(item && item.status)

    },

    statusWithRevision(item) {

      return formatStatusWithRevisionCount(item && item.status, this.revisionCountOf(item))

    },

    showHistoryAction(item) {

      return this.isRevisedRow(item) || this.revisionCountOf(item) > 0

    },

  }

}

</script>



<style lang="scss">

@import './invoice-dense-tab.scss';



$inv-teal: #0099AC;

$inv-muted: #64748b;



.invoice-overview-tab {

  .inv-candidate-line {

    line-height: 1.25;

  }



  .inv-signed-pill {

    display: inline-flex;

    align-items: center;

    margin-top: 0.15rem;

    font-weight: 650;

    color: #2f9e6f;

  }

  .inv-rev-pill {

    display: inline-flex;

    align-items: center;

    margin-top: 0.15rem;

    font-size: 0.72rem;

    font-weight: 650;

    color: #0f766e;

  }



  .inv-inline-action {

    font-weight: 650;

    color: $inv-muted !important;

    height: auto;

    line-height: 1.2;



    &:hover {

      color: $inv-teal !important;

    }

  }



  .inv-more-toggle {

    width: 1.75rem;

    height: 1.75rem;

    display: inline-flex;

    align-items: center;

    justify-content: center;

  }

}

@media (max-width: 767.98px) {
  .invoice-overview-tab {
    .inv-more-toggle {
      width: 2.5rem;
      height: 2.5rem;
    }

    .inv-inline-action {
      min-height: 2.5rem;
      display: inline-flex;
      align-items: center;
    }
  }
}

</style>


